/**
 * Sign-in shell. Four variants a broker can pick from
 * (`tenant.settings.authLayout`, set in /admin/settings?tab=auth-layout):
 *
 *   split           — brand panel left, form right (default)
 *   centered        — one card on a quiet ground
 *   fullscreen-hero — uploaded hero image, card over it
 *   minimal         — form only
 *
 * The split variant carries the brand moment — a midnight panel with the
 * bearing dial and the "Trade · Grow · Succeed" line. None of them state
 * spreads, execution or returns.
 */
import type { CSSProperties } from "react";
import { Outlet } from "react-router-dom";
import { useCurrentTenant } from "@/hooks/useTenant";
import type { AuthLayoutVariant } from "@/types/tenantSettings";
import { Brand } from "./Brand";
import { CompassDial } from "./CompassDial";

/** Capability statements, not performance promises. */
const POINTS = [
  "Open live and demo trading accounts",
  "Deposit, withdraw and transfer from one wallet",
  "Track your partner network and commissions",
  "Support that answers with specifics",
];

const YEAR = new Date().getFullYear();

export function AuthLayout() {
  const { data: tenantInfo } = useCurrentTenant();
  const variant: AuthLayoutVariant = tenantInfo?.tenant?.settings?.authLayout ?? "split";
  const tagline = tenantInfo?.tenant?.settings?.branding?.tagline;
  const heroUrl = tenantInfo?.tenant?.settings?.branding?.authBackgroundUrl;

  if (variant === "centered") return <CenteredLayout tagline={tagline} heroUrl={heroUrl} />;
  if (variant === "minimal") return <MinimalLayout />;
  if (variant === "fullscreen-hero") return <FullscreenHeroLayout heroUrl={heroUrl} tagline={tagline} />;
  return <SplitLayout tagline={tagline} heroUrl={heroUrl} />;
}

function Footer({ className = "" }: { className?: string }) {
  const { data } = useCurrentTenant();
  const name = data?.tenant && !data.tenant.isPlatform ? data.tenant.name : "Wachsen Markets";
  return <p className={`text-xs text-fg-subtle ${className}`}>© {YEAR} {name}</p>;
}

/* ─── Split (default) ──────────────────────────────────────────────── */
function SplitLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div className="grid min-h-screen bg-bg lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
      {/* Brand panel — midnight in both appearances */}
      <aside
        className="relative hidden overflow-hidden bg-[#10243A] text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14"
        style={
          heroUrl
            ? { backgroundImage: `linear-gradient(rgba(11,15,20,.86), rgba(11,15,20,.86)), url(${heroUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
            : undefined
        }
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_1px_1px,rgba(192,198,212,.09)_1px,transparent_0)] [background-size:22px_22px]" />
        <CompassDial className="pointer-events-none absolute -right-[34%] top-1/2 h-[112%] max-h-[900px] w-auto -translate-y-1/2 opacity-80" />
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-[62%] bg-gradient-to-r from-[#10243A] via-[#10243A]/85 to-transparent" />

        <div className="relative"><Brand onDark size="lg" /></div>

        <div className="relative max-w-lg">
          <p className="font-display text-[11px] font-semibold uppercase tracking-[0.32em] text-[#C0C6D4]/70">
            Trade <span className="text-[#08B9A8]">·</span> Grow <span className="text-[#0BACD4]">·</span> Succeed
          </p>
          <h1 className="mt-5 font-display text-[44px] font-extrabold leading-[1.05] tracking-[-0.02em] xl:text-[54px]">
            {tagline ?? (<>Global Markets.<br /><span className="text-split">Smarter Trading.</span></>)}
          </h1>
          <ul className="mt-9 grid max-w-md grid-cols-2 gap-x-6 gap-y-4 text-[13px] text-[#C0C6D4]">
            {POINTS.map((p, i) => (
              <li key={p} className="flex gap-3">
                <span aria-hidden className="mt-[3px] font-display text-[10px] font-bold text-[#08B9A8]">0{i + 1}</span>
                {p}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-[#C0C6D4]/60">© {YEAR} Wachsen Markets</p>
      </aside>

      <main className="relative flex flex-col bg-bg p-6 sm:p-10">
        <div className="flex items-center justify-between lg:hidden">
          <Brand />
        </div>
        <div className="grid flex-1 place-items-center py-10">
          <div className="w-full max-w-[380px] animate-rise-in">
            <Outlet />
          </div>
        </div>
        <Footer className="lg:hidden" />
      </main>
    </div>
  );
}

/* ─── Centered ─────────────────────────────────────────────────────── */
function CenteredLayout({ tagline, heroUrl }: { tagline?: string; heroUrl?: string }) {
  return (
    <div
      className="grid min-h-screen place-items-center bg-bg-subtle p-4"
      style={
        heroUrl
          ? {
              backgroundImage: `linear-gradient(hsl(var(--bg-subtle) / 0.9), hsl(var(--bg-subtle) / 0.9)), url(${heroUrl})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : undefined
      }
    >
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-raised">
          <div className="mb-7 flex flex-col items-center text-center">
            <Brand size="lg" />
            {tagline && <p className="mt-4 max-w-xs text-sm text-fg-muted">{tagline}</p>}
          </div>
          <Outlet />
        </div>
        <div className="mt-4 text-center"><Footer /></div>
      </div>
    </div>
  );
}

/* ─── Fullscreen hero ──────────────────────────────────────────────── */
function FullscreenHeroLayout({ heroUrl, tagline }: { heroUrl?: string; tagline?: string }) {
  const bg: CSSProperties = heroUrl
    ? {
        backgroundImage: `linear-gradient(hsl(var(--bg) / 0.75), hsl(var(--bg) / 0.75)), url(${heroUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  return (
    <div className="grid min-h-screen place-items-center bg-bg-subtle p-4 bearing-grid" style={bg}>
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border bg-card p-8 shadow-overlay">
          <div className="mb-7 flex flex-col items-center text-center">
            <Brand size="lg" />
            {tagline && <p className="mt-4 max-w-xs text-sm text-fg-muted">{tagline}</p>}
          </div>
          <Outlet />
        </div>
        <div className="mt-4 text-center"><Footer /></div>
      </div>
    </div>
  );
}

/* ─── Minimal ──────────────────────────────────────────────────────── */
function MinimalLayout() {
  return (
    <div className="grid min-h-screen place-items-center bg-bg p-4">
      <div className="w-full max-w-sm">
        <div className="mb-10 flex justify-center"><Brand size="lg" /></div>
        <Outlet />
        <div className="mt-8 text-center"><Footer /></div>
      </div>
    </div>
  );
}
